import { filledInputClasses } from '@mui/material/FilledInput';
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import { formLabelClasses } from '@mui/material/FormLabel';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import type { Components, Theme } from '@mui/material/styles';
import { varAlpha } from 'minimal-shared/utils';

// ----------------------------------------------------------------------

const MuiInputBase: Components<Theme>['MuiInputBase'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      [`&.${inputBaseClasses.disabled}`]: { '& svg': { color: theme.vars.palette.text.disabled } },
      [`& .${inputBaseClasses.input}:focus`]: { borderRadius: 'inherit' },
    }),
    input: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(14),
      padding: '10px 12px',
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(16),
      },
      '&::placeholder': { opacity: 1, color: theme.vars.palette.text.disabled },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiInput: Components<Theme>['MuiInput'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    underline: ({ theme }) => ({
      '&::before': { borderBottomColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32) },
      '&::after': { borderBottomColor: theme.vars.palette.text.primary },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiOutlinedInput: Components<Theme>['MuiOutlinedInput'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  // The label now sits above the field, so the outline must not carve out a notch for it.
  defaultProps: { notched: false },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: '10px',
      [`&.${outlinedInputClasses.focused}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.5),
          borderWidth: '1px',
        },
      },
      [`&.${outlinedInputClasses.error}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.error.light,
        },
      },
      [`&.${outlinedInputClasses.error}:hover .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.vars.palette.error.light,
        borderWidth: '1px',
      },
      [`&.${outlinedInputClasses.disabled}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.action.disabledBackground,
        },
      },
      [`&:hover:not(.${outlinedInputClasses.disabled}):not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
        {
          borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.5),
          borderWidth: '1px',
        },
    }),
    input: { padding: '10px 12px' },
    notchedOutline: ({ theme }) => ({
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
      transition: theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.shortest,
      }),
      // Collapse the legend so the top border stays continuous (no leftover notch gap) now that the
      // label lives above the field rather than inside the outline.
      '& legend': { width: 0, maxWidth: 0 },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiFilledInput: Components<Theme>['MuiFilledInput'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { disableUnderline: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
      '&:hover': { backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16) },
      [`&.${filledInputClasses.focused}`]: {
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
      },
      [`&.${filledInputClasses.error}`]: {
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
        [`&.${filledInputClasses.focused}`]: {
          backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.16),
        },
      },
      [`&.${filledInputClasses.disabled}`]: {
        backgroundColor: theme.vars.palette.action.disabledBackground,
      },
    }),
  },
};

// PickersOutlinedInput is a separate component from MuiOutlinedInput and does not inherit its
// theme overrides. The component uses MuiPickersOutlinedInput-focused (not Mui-focused) and
// derives its focus color from palette variants. We override those styles here to match
// the rest of the form fields: grey border, 1px width, and our custom input height.
const MuiPickersOutlinedInput: Components<Theme>['MuiPickersOutlinedInput'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: '10px',
      // MUI X pads the picker to `0 14px`; every other field indents its value by the 12px that
      // MuiOutlinedInput.input uses, so a date sitting in a stacked form was two pixels off the
      // fields above and below it.
      padding: '0 12px',
      // The component's variant style for inputColor:'primary' uses the same selector but without
      // !important. We use !important to guarantee our grey border wins regardless of injection order.
      // Note: pickersOutlinedInputClasses.focused === 'Mui-focused' (global class, not component-specific).
      '&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: `${varAlpha(theme.vars.palette.grey['500Channel'], 0.5)} !important`,
        borderWidth: '1px !important',
      },
      '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: theme.vars.palette.error.light,
      },
      '&.Mui-error:hover .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: theme.vars.palette.error.light,
        borderWidth: '1px',
      },
      '&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: theme.vars.palette.action.disabledBackground,
      },
      '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.5),
        borderWidth: '1px',
      },
    }),
    // DatePicker renders section divs instead of a real <input>, so height is controlled by
    // sectionsContainer padding — match the 10px used by MuiOutlinedInput.input.
    sectionsContainer: { padding: '10px 0' },
    notchedOutline: ({ theme }) => ({
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
      transition: theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.shortest,
      }),
      // Collapse the notch: the picker's label now sits above the field like every other input.
      '& legend': { width: 0, maxWidth: 0 },
    }),
  },
};
const MuiTextField: Components<Theme>['MuiTextField'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { variant: 'outlined', size: 'medium' },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {},
};

// ----------------------------------------------------------------------

const MuiFormLabel: Components<Theme>['MuiFormLabel'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(14),
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(16),
      },
      color: theme.vars.palette.text.secondary,
      [`&.${formLabelClasses.focused}`]: { color: theme.vars.palette.text.secondary },
      [`&.${formLabelClasses.error}`]: { color: theme.vars.palette.error.main },
      [`&.${formLabelClasses.disabled}`]: { color: theme.vars.palette.text.disabled },
    }),
  },
};

const MuiInputLabel: Components<Theme>['MuiInputLabel'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  // Force the "shrunk" state so the label never floats inside the field as a placeholder; combined with
  // the static positioning below, this renders every field label as a block ABOVE the input.
  defaultProps: { shrink: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    // App-wide outside-label style: the label sits above the field (matching the form-fields FieldLabel
    // used by the policy forms) instead of MUI's default notched in-input label. MUI's FormControl is a
    // flex column, so a statically positioned label naturally stacks above the input.
    root: ({ theme }) => ({
      ...theme.typography.subtitle2,
      position: 'static',
      transform: 'none',
      transformOrigin: 'top left',
      maxWidth: '100%',
      marginBottom: theme.spacing(0.75),
      pointerEvents: 'auto',
      color: theme.vars.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(16),
      },
      [`&.${formLabelClasses.focused}`]: { color: theme.vars.palette.text.primary },
      [`&.${formLabelClasses.error}`]: { color: theme.vars.palette.error.main },
      [`&.${formLabelClasses.disabled}`]: { color: theme.vars.palette.text.disabled },
      // Beat MUI's float transforms in every variant/state so the label stays put above the field.
      [`&.${inputLabelClasses.formControl}`]: { position: 'static', transform: 'none' },
      [`&.${inputLabelClasses.outlined}`]: { transform: 'none' },
      [`&.${inputLabelClasses.outlined}.${inputLabelClasses.shrink}`]: { transform: 'none' },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiFormHelperText: Components<Theme>['MuiFormHelperText'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      // Adjust vertical spacing below the input
      marginTop: theme.spacing(0.4),
      marginBottom: theme.spacing(1.2),
      fontSize: theme.typography.pxToRem(12),
      color: theme.vars.palette.text.secondary,
      fontStyle: 'italic',
      // MUI indents helper text by 14px for filled/outlined fields so it clears a label sitting inside
      // the outline. Our labels sit above the field, so that indent just knocks the hint out of
      // alignment with the label and the input's left edge.
      [`&.${formHelperTextClasses.contained}`]: { marginLeft: 0, marginRight: 0 },
    }),
  },
};

// ----------------------------------------------------------------------

export const textfield = {
  MuiInput,
  MuiInputBase,
  MuiFilledInput,
  MuiOutlinedInput,
  MuiPickersOutlinedInput,
  MuiTextField,
  MuiFormLabel,
  MuiInputLabel,
  MuiFormHelperText,
};
